import { Badge } from '@wartek-id/badge'
import { Shimmer } from '@wartek-id/shimmer'
import { Text } from '@wartek-id/text-v1'

import { useAPIGetObservationImplementation } from 'api/kinerja/useAPIKinerja'
import {
  TableBody,
  TableHeader,
  TableHeaderItem,
  TablePelaksanaanKinerja,
  TableRow,
  TableRowItem,
} from 'app/KinerjaPage/components/TablePelaksanaanKinerja'
import { useKinerjaTipe } from 'app/KinerjaShared/hooks/useKinerjaTipe'
import { HASIL_OBSERVASI_PRAKTIK_KINERJA_PEGAWAI_BADGE } from 'app/KinerjaShared/constants/praktikKinerja'

import type { MultiPeriodPayload } from 'types/kinerja'

type HasilObservasiPraktikKinerjaPegawaiTableProps = {
  periodParams?: MultiPeriodPayload
  ptkId?: string
}

export function HasilObservasiPraktikKinerjaPegawaiTable({
  periodParams,
  ptkId,
}: HasilObservasiPraktikKinerjaPegawaiTableProps) {
  const { tipe } = useKinerjaTipe()

  // -----------------------------------------
  // Fetch hasil observasi data
  // -----------------------------------------
  const { data, isLoading } = useAPIGetObservationImplementation(
    { ptkId, tipe, ...periodParams },
    {
      useErrorBoundary: true,
      staleTime: 6 * 60 * 60 * 1000, // 6 hour
    }
  )

  return (
    <div className="space-y-6">
      <Text>
        <strong>Perlu diperhatikan bahwa:</strong>
        <br />
        Hasil Observasi hanya ditujukan untuk memandu diskusi Rencana Tindak
        Lanjut.
      </Text>

      <TablePelaksanaanKinerja>
        <TableHeader>
          <TableHeaderItem>No.</TableHeaderItem>
          <TableHeaderItem>Target Perilaku</TableHeaderItem>
          <TableHeaderItem>Rating</TableHeaderItem>
          <TableHeaderItem>Catatan</TableHeaderItem>
        </TableHeader>

        <TableBody>
          {isLoading ? (
            <TableRowShimmer />
          ) : (
            data?.data?.detail?.behavioralTargets?.map((item, i) => {
              const badge =
                HASIL_OBSERVASI_PRAKTIK_KINERJA_PEGAWAI_BADGE[
                  item.observationResult
                ]

              return (
                <TableRow key={item.id}>
                  <TableRowItem>{i + 1}</TableRowItem>
                  <TableRowItem className="w-5/12 overflow-wrap-anywhere">
                    {item.behavioralTarget}
                  </TableRowItem>
                  <TableRowItem>
                    <Badge
                      variant={badge?.variant ?? 'neutral'}
                      className="whitespace-nowrap"
                    >
                      {badge?.text ?? '-'}
                    </Badge>
                  </TableRowItem>
                  <TableRowItem className="overflow-wrap-anywhere">
                    {item.observationNote || '-'}
                  </TableRowItem>
                </TableRow>
              )
            })
          )}
        </TableBody>
      </TablePelaksanaanKinerja>

      <div className="space-y-2">
        <Text variant="heading-sm">Rekomendasi</Text>
        <Text color="subdued" className="overflow-wrap-anywhere">
          {isLoading ? (
            <Shimmer className="mb-2 !w-full" noOfLines={4} />
          ) : (
            data?.data?.detail?.superiorRecommendation || '-'
          )}
        </Text>
      </div>
    </div>
  )
}

function TableRowShimmer() {
  return (
    <>
      {Array(3)
        .fill('')
        .map((_x, i) => (
          <TableRow key={i}>
            <TableRowItem className="text-center">
              <Shimmer className="!w-5" />
            </TableRowItem>
            <TableRowItem className="w-5/12">
              <Shimmer className="!w-full" />
            </TableRowItem>
            <TableRowItem>
              <Shimmer />
            </TableRowItem>
            <TableRowItem>
              <Shimmer />
            </TableRowItem>
          </TableRow>
        ))}
    </>
  )
}
